import { useEffect, useRef } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../hooks/useTheme';
import { COLORS, FONTS, SHADOWS } from '../styles/tokens';

interface ProfileDropdownProps {
  onClose: () => void;
  onOpenProfile?: () => void;
  onOpenSecurity?: () => void;
  pictureUrl?: string | null;
}

/** Profile dropdown menu anchored below the avatar */
export function ProfileDropdown({ onClose, onOpenProfile, onOpenSecurity, pictureUrl }: ProfileDropdownProps): React.JSX.Element {
  const { isDark } = useTheme();
  const { username, fullName, roles, logout } = useAuth();
  const colors = isDark ? COLORS.dark : COLORS.light;
  const isAdmin = roles.includes('admin');
  const dropdownRef = useRef<HTMLDivElement>(null);

  /** Close on outside click */
  useEffect(() => {
    function handleMouseDown(e: MouseEvent): void {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onClose]);

  const menuItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '10px 16px',
    border: 'none',
    backgroundColor: 'transparent',
    color: colors.text,
    fontFamily: FONTS.body,
    fontSize: '13px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.15s',
  };

  function handleHover(e: React.MouseEvent<HTMLButtonElement>, enter: boolean): void {
    e.currentTarget.style.backgroundColor = enter
      ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)')
      : 'transparent';
  }

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'absolute',
        top: '40px',
        right: 0,
        width: '240px',
        backgroundColor: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: '12px',
        boxShadow: isDark ? SHADOWS.neumorphicDark : SHADOWS.neumorphicLight,
        zIndex: 100,
        overflow: 'hidden',
      }}
    >
      {/* User info header */}
      <div style={{ padding: '16px', borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: isDark ? COLORS.accent.blueDark : COLORS.accent.blue,
              display: 'grid',
              placeItems: 'center',
              color: '#FFFFFF',
              fontFamily: FONTS.heading,
              fontSize: '16px',
              fontWeight: 600,
              border: isAdmin ? `2px solid ${COLORS.accent.gold}` : 'none',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {pictureUrl ? (
              <img src={pictureUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              (fullName || username || 'U').charAt(0).toUpperCase()
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontFamily: FONTS.body, fontSize: '14px', fontWeight: 600, color: colors.text, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {fullName || username}
            </p>
            <p style={{ fontFamily: FONTS.body, fontSize: '11px', color: isAdmin ? COLORS.accent.gold : colors.textSecondary, margin: '2px 0 0', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {isAdmin ? 'Administrator' : 'User'}
            </p>
          </div>
        </div>
      </div>

      {/* Menu items */}
      <div style={{ padding: '4px 0' }}>
        <button
          style={menuItemStyle}
          onMouseEnter={e => handleHover(e, true)}
          onMouseLeave={e => handleHover(e, false)}
          onClick={() => { onClose(); onOpenProfile?.(); }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.textSecondary} strokeWidth="1.8" strokeLinecap="round">
            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          Profile Settings
        </button>

        <button
          style={menuItemStyle}
          onMouseEnter={e => handleHover(e, true)}
          onMouseLeave={e => handleHover(e, false)}
          onClick={() => { onClose(); onOpenSecurity?.(); }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.textSecondary} strokeWidth="1.8" strokeLinecap="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          Account Security
        </button>
      </div>

      {/* Divider */}
      <div style={{ borderTop: `1px solid ${colors.border}`, margin: '0' }} />

      {/* Sign Out */}
      <div style={{ padding: '4px 0' }}>
        <button
          style={{ ...menuItemStyle, color: '#EF4444' }}
          onMouseEnter={e => handleHover(e, true)}
          onMouseLeave={e => handleHover(e, false)}
          onClick={logout}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="1.8" strokeLinecap="round">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Sign Out
        </button>
      </div>
    </div>
  );
}
